// @flow

import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Track, type TrackingEventAJAXProps } from '@ta/common.tracking';
import { localize } from '@ta/common.i18n';
import { SocialLoginGate } from '@ta/social.login-gate';
import Button from '@ta/styleguide.button';

import styles from './LoginBar.less';
/**
 *
 * This is a login bar that pops up at the bottom of the page. TRVX-11324
 * Only contains UI logic here.
 *
 * */

type Props = {|
  /* Boolean that changes from false to true to set off the transition into view */
  shouldShow: boolean,

  /* Optional tracking logic when clicking the login button */
  trackingOnLoginClick?: TrackingEventAJAXProps,

  /* Optional tracking logic when clicking the join button */
  trackingOnJoinClick?: TrackingEventAJAXProps,

  /* Optional tracking logic when element is shown */
  trackingOnShow?: TrackingEventAJAXProps,

  /* When this property is true, the component behaves completely the same, except nothing is visible. Used for testing purposes */
  isGhost?: boolean,

  /* Optional prop for tracking id */
  productId?: number,
|};

const LoginBar = ({
  shouldShow,
  trackingOnLoginClick,
  trackingOnJoinClick,
  trackingOnShow,
  isGhost,
  productId,
}: Props) => (
  <CSSTransition
    timeout={500}
    classNames={{
      enter: styles.slideInEnter,
      enterActive: styles.slideInEnterActive,
    }}
    in={shouldShow}
    unmountOnExit
  >
    <div className={`${styles.container} ${isGhost ? styles.ghost : ''}`}>
      <Track mount={{ once: true, ...trackingOnShow }}>
        <span className={styles.leftText}>
          {/* Discover amazing ideas from friends and travel experts */}
          {localize('social_bar_prompt')}
        </span>
      </Track>
      <div className={styles.rightSide}>
        <SocialLoginGate flowOrigin="login" pid={productId}>
          {loginGate => (
            <div className={styles.loginButton}>
              <Track click={{ once: false, ...trackingOnLoginClick }}>
                <Button
                  onClick={() => {
                    loginGate();
                  }}
                  type="none"
                  size="large"
                >
                  {/* Log in */}
                  <span>{localize('social_bar_log_in')}</span>
                </Button>
              </Track>
            </div>
          )}
        </SocialLoginGate>
        <SocialLoginGate flowOrigin="join" pid={productId}>
          {loginGate => (
            <Track click={{ once: false, ...trackingOnJoinClick }}>
              <Button
                onClick={() => {
                  loginGate();
                }}
                type="secondary"
                size="large"
              >
                {/* Join */}
                <span>{localize('social_bar_join')}</span>
              </Button>
            </Track>
          )}
        </SocialLoginGate>
      </div>
    </div>
  </CSSTransition>
);

LoginBar.defaultProps = {
  trackingOnLoginClick: undefined,
  trackingOnJoinClick: undefined,
  trackingOnShow: undefined,
  isGhost: false,
  productId: undefined,
};

export default LoginBar;
